import React, { useState, useEffect, useCallback } from "react";
import { PaymentMethodData } from "../network/api/PaymentMethodData";
import { GenerateOTP } from "../../../../../../auth/network/api/GenerateOTP";
import OtpInput from "../../../../../../auth/pages/register/components/OtpInput";
import { useAppContext } from "../../../../../../../context/AppContext";
import { AnimatePresence } from "framer-motion";
import Transition from "../../../../../../../components/Transition";
import ButtonLoader from "../../../../../../../components/ButtonLoader";
import { PaymentMethodsData } from "../network/api/PaymentMethodsData";

export default function PaymentMethods({ setSelectedMethodObject }) {
    const { appData } = useAppContext();

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState("paypal");
    const [paypalEmail, setPaypalEmail] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [step, setStep] = useState("form");
    const [otp, setOtp] = useState("");

    const [methodsLoading, setMethodsLoading] = useState(false);
    const [methodsError, setMethodsError] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);


    const handleAddMethod = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await GenerateOTP({ email: paypalEmail.trim(), app_id: appData.id, not_auth: true });
            if (response.status === "success") {
                setVerificationCode(response.data.otp);
                setStep("verify");
            } else {
                setError(response.message || "Failed to send verification code.");
            }
        } catch (error) {
            setError("An error occurred while sending the verification code. Please try again later.");
        }
        setIsLoading(false);
    }

    const handleConfirmCode = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        if (otp.length !== 5) {
            setError("Please enter a valid 5-digit OTP");
            setIsLoading(false);
            return;
        }
        if (otp !== verificationCode) {
            setError("Please enter a valid OTP which is sent to your email");
            setIsLoading(false);
            return;
        }

        try {
            const response = await PaymentMethodData({
                method: "POST",
                formData: {
                    method: selectedMethod,
                    account_details: selectedMethod === "paypal" ? JSON.stringify({ "email": `${paypalEmail}` }) : null,
                }
            });
            
            if (response.status === "success") {
                setStep("confirmed");
            } else {
                setError(response.message || "Failed to add payment method.");
            }
        }
        catch (err) {
            setError(err.message || "An error occurred while adding the payment method. Please try again later.");
        }
        setIsLoading(false);
    }, [otp, verificationCode, selectedMethod, paypalEmail]);

    useEffect(() => {
        setError(null);
        if (otp.length === 5) {
            handleConfirmCode();
        }

    }, [otp, handleConfirmCode]);

    const handleDeleteMethod = async (method) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await PaymentMethodData({ method: "DELETE", id: method.id });
            if (response.status === "success") {
                setPaymentMethods((prevMethods) => prevMethods.filter((m) => m !== method));
                if (selectedPaymentMethodId === method.id) {
                    setSelectedPaymentMethodId(null);
                    setSelectedMethodObject(null);
                }
            } else {
                setError(response.message || "Failed to delete payment method.");
            }
        } catch (error) {
            setError("An error occurred while deleting the payment method. Please try again later.");
        }
        setIsLoading(false);
    };

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            setMethodsLoading(true);
            setMethodsError(null);
            try {
                const response = await PaymentMethodsData();
                if (response.status === "success") {
                    setPaymentMethods(response.data || []);
                } else {
                    setMethodsError(response.message || "Failed to fetch payment methods.");
                }
            } catch (error) {
                setMethodsError("An error occurred while fetching payment methods. Please try again later.");
            }
            setMethodsLoading(false);
        };

        fetchPaymentMethods();


    }, [step]);

    const handlePaymentMethodChange = async (e) => {
        const value = e.target.value;
        setSelectedMethod(value);
        setError(null);
        setOtp("");
        setPaypalEmail("");
        setVerificationCode("");

        if (value === "licence") {
            setIsLoading(true);
            
            try {
                const response = await PaymentMethodData({
                    method: "POST",
                    formData: {
                        method: "licence",
                        account_details: JSON.stringify({ "email": "Convert To Licence Keys" }) ,
                    }
                });
                
                if (response.status === "success") {
                    setStep("confirmed");
                } else {
                    setError(response.message || "Failed to add payment method.");
                }
            }
            catch (err) {
                setError(err.message || "An error occurred while adding the payment method. Please try again later.");
            }
            finally {
                setIsLoading(false);
            }

        } else {
            setStep("form");
        }
    };

    return (
        <>
            <div className="card-body">
                <h4 className="card-title">Payment Methods</h4>
                <p className="text-muted mb-4">You can withdraw your earnings using the following methods:</p>
                {methodsLoading && (

                    <div className="text-center mb-3" key={`loading-spinner-withdrawals-hgfdg`}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}
                {methodsError && (
                    <AnimatePresence mode="wait" key={`methods-error-${methodsError}`}>
                        <Transition key={methodsError}>
                            <div className="alert alert-danger alert-dismissible alert-alt solid fade show">
                                <strong>Error!</strong> {methodsError}
                            </div>
                        </Transition>
                    </AnimatePresence>
                )}
                {paymentMethods?.map((method, index) => (
                    <div 
                        key={`payment-method-sdjfhk-${index}`} 
                        className={`${method.method ==="licences" ? "" : ""} d-flex align-items-center mb-3 border p-3 rounded shadow-sm position-relative ${selectedPaymentMethodId === method.id ? 'bg-primary text-white' : ''}`}
                        onClick={() => {
                            if (selectedPaymentMethodId === method.id) {
                                setSelectedPaymentMethodId(null);
                                setSelectedMethodObject(null);
                            } else {
                                setSelectedPaymentMethodId(method.id);
                                setSelectedMethodObject(method);
                            }
                        }}
                        style={{ cursor: "pointer" }}
                    >
                        <img
                            src={method.method ==="paypal" ? "https://images.ctfassets.net/y6oq7udscnj8/7pGYJSsSu8IjvuscnxPcng/ae9dc800b649640406b5bfa1ae9b02d6/PayPal.png?w=592&h=368&q=50&fm=png" : "https://robotrader.take-profit-signals.co.za/uploads/robotrader/2.png"}
                            alt="licences"
                            className="me-3 rounded"
                            style={{ width: "80px", height: "auto", marginRight: "1rem" }}
                        />
                        <div className="flex-grow-1">
                            <h6 className={`${selectedPaymentMethodId === method.id ? 'text-white' : ''}`}>{method.method}</h6>
                            <small >
                                {(() => {
                                    try {
                                        const parsed = JSON.parse(method?.account_details);
                                        return parsed?.email || "No email provided";
                                    } catch {
                                        return method?.account_details || "No email provided";
                                    }
                                })()}
                            </small>
                        </div>
                        <div className="dropdown custom-dropdown mb-0">
                            <div className="btn sharp btn-primary tp-btn" data-bs-toggle="dropdown">
                                <svg width="18px" height="18px" viewBox="0 0 24 24">
                                    <circle fill="#000" cx="12" cy="5" r="2"></circle>
                                    <circle fill="#000" cx="12" cy="12" r="2"></circle>
                                    <circle fill="#000" cx="12" cy="19" r="2"></circle>
                                </svg>
                            </div>
                            <div className="dropdown-menu dropdown-menu-end">
                                <button className="dropdown-item text-danger" onClick={() => handleDeleteMethod(method)}>Delete</button>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="text-center">
                    <button className="btn btn-outline text-primary w-100" onClick={() => { setStep("form"); setError(null) }} data-bs-toggle="modal" data-bs-target="#addPaymentMethod">
                        <i className="fa fa-plus me-2"></i> Add New Payment Method
                    </button>
                </div>
            </div>

            <div className="modal fade" id="addPaymentMethod">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content items-center align-items-center justify-content-center text-center">
                        <div className="modal-header">
                            <h5 className="modal-title">Add New Payment Method</h5>
                        </div>
                        <div className="modal-body w-100">
                            {!isLoading && error ? (
                                <AnimatePresence mode="wait">
                                    <Transition key={error} >
                                        <div className="alert alert-danger alert-dismissible alert-alt solid fade show">
                                            <strong>Error!</strong> {error}
                                        </div>
                                    </Transition>
                                </AnimatePresence>
                            ) : null}
                            {step === "form" && (
                                <>
                                    <h6>Select Method</h6>
                                    <select className="form-select mb-3" id="paymentMethodSelect" defaultValue="paypal" 
                                    onChange={handlePaymentMethodChange}>
                                        <option value="licence">Licence Keys</option>
                                        <option value="paypal">PayPal</option>
                                    </select>
                                    <div className="mb-3">
                                        <label htmlFor="paymentDetails" className="form-label">Paypal Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="paymentDetails"
                                            placeholder="email@paypal.com"
                                            required
                                            value={paypalEmail}
                                            onChange={(e) => setPaypalEmail(e.target.value)}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <button type="button" className="btn btn-success w-100" onClick={handleAddMethod} disabled={isLoading || !paypalEmail}>{isLoading ? <ButtonLoader /> : "Send Verification Code"}</button>
                                </>
                            )}
                            {step === "verify" && (
                                <>
                                    <h6>Verify Email</h6>
                                    <p className="text-muted">A verification code has been sent to {paypalEmail}</p>
                                    <div className="mb-3">
                                        <OtpInput
                                            length={5}
                                            onChange={(value) => setOtp(value)}
                                            isLoading={isLoading}
                                        />
                                    </div>
                                    <button type="button" className="btn btn-success w-100" onClick={handleConfirmCode} disabled={isLoading || otp.length < 5 || error}>{isLoading ? <ButtonLoader /> : "Verify"}</button>
                                </>
                            )}
                            {step === "confirmed" && (
                                <>
                                    <h6>Payment Method Added Successfully</h6>
                                    <i className="fa fa-check-circle text-success mb-3" style={{ fontSize: "3rem" }}></i>
                                    <button type="button" className="btn btn-primary w-100" data-bs-dismiss="modal">Close</button>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-dark light" data-bs-dismiss="modal">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}